import React, { forwardRef } from "react";
import { ExternalLink, Copy } from "lucide-react";
import { fmtDate } from "../ExcelUploadManager";
import ExcelFilterBar from "./ExcelFilterBar";
import ExcelShipmentRows from "./ExcelShipmentRows";
import PlatformShipmentsTable from "./PlatformShipmentsTable";

const TABS = [["inbound","Inbound"],["outbound","Outbound"],["platform","Platform"]];

// "Live Shipments & ASNs": report tabs, Copy table, filters and rows
const ShipmentsTable = forwardRef(function ShipmentsTable(
    { activeTab, activeView, excelReports, rows, statusLabels, liveShipments, loading, tenantOnly, onSwitchTab, onUpload, onOpenRow },
    ref,
) {
    const report = excelReports[activeTab];
    const isExcel = activeTab !== "platform";
    const { rows: data } = rows;

    const subtitle = isExcel
        ? (rows.loading ? "Loading…"
            : `${data.total}${rows.filtersActive ? " matching" : ""} record${data.total !== 1 ? "s" : ""} from the ${activeTab} report of ${fmtDate(report?.reportDate)}${data.total > data.rows.length ? ` · showing ${data.rows.length}` : ""} · click a row for its history`)
        : loading ? "Loading from Firestore…"
        : liveShipments.length === 0 ? "No records yet"
        : `${liveShipments.length} most recent records`;

    return (
        <div ref={ref} className="bg-[#111827] border border-[#1F2937] rounded-xl overflow-hidden scroll-mt-4">
            <div className="flex items-center justify-between px-5 py-4 gap-3 flex-wrap">
                <div>
                    <p className="text-sm font-bold text-white">Live Shipments &amp; ASNs</p>
                    <p className="text-[11px] text-slate-500">{subtitle}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex bg-[#0D1520] border border-[#1F2937] rounded-lg p-0.5">
                        {TABS
                            .filter(([id]) => id !== "platform" || (activeView !== "all" && !tenantOnly))
                            .map(([id, label]) => (
                            <button key={id} onClick={() => onSwitchTab(id)}
                                className={`px-2.5 py-1 rounded-md text-[11px] transition-colors ${activeTab === id ? "bg-[#1F2937] text-white" : "text-slate-500 hover:text-slate-300"}`}>
                                {label}{id !== "platform" && !excelReports[id] && <span className="text-slate-700"> ·</span>}
                            </button>
                        ))}
                    </div>
                    {isExcel && report && (
                        <button onClick={rows.copyTable} disabled={rows.copyMsg === "Copying…"}
                            title="Copy every row matching the current filters — paste straight into Excel"
                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#374151] text-[11px] text-slate-300 hover:border-slate-500 disabled:opacity-50">
                            <Copy size={12}/> {rows.copyMsg || "Copy table"}
                        </button>
                    )}
                    {!isExcel && (
                        <button className="text-[11px] text-blue-400 hover:underline flex items-center gap-1">
                            View All <ExternalLink size={10}/>
                        </button>
                    )}
                </div>
            </div>
            {isExcel && report && <ExcelFilterBar reportType={activeTab} rows={rows} statusLabels={statusLabels}/>}
            {isExcel && (
                // Rows scroll inside the card (header row stays pinned) instead of stretching the page
                <div className="overflow-auto max-h-[70vh] overscroll-contain">
                    {!report ? (
                        <div className="px-4 py-8 text-center text-slate-500 text-sm border-t border-[#1F2937]">
                            No {activeTab} report imported yet.{" "}
                            <button onClick={onUpload} className="text-blue-400 hover:underline">Upload one</button>
                        </div>
                    ) : (
                        <ExcelShipmentRows reportType={activeTab} rows={rows} statusLabels={statusLabels} onOpenRow={onOpenRow}/>
                    )}
                </div>
            )}
            {!isExcel && <PlatformShipmentsTable shipments={liveShipments} loading={loading}/>}
        </div>
    );
});

export default ShipmentsTable;
